/**
 * Customer P&L — Stripe-powered, real per-customer profitability
 */
import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import SEOHead from '../components/SEOHead';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  TrendingUp, AlertTriangle, RefreshCcw, Download, Search, Zap,
  ChevronRight, CheckCircle2, XCircle, Bell, Sparkles, Activity, FileDown, Upload,
  ShieldOff, ShieldCheck,
} from 'lucide-react';
import {
  ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Legend, AreaChart, Area,
} from 'recharts';
import { toast } from 'sonner';
import BillDetectiveCrossLink from '../components/BillDetectiveCrossLink';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtCurrency = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${n < 0 ? '-' : ''}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${n < 0 ? '-' : ''}$${(abs / 1_000).toFixed(1)}K`;
  return `${n < 0 ? '-' : ''}$${abs.toFixed(0)}`;
};

const fmtPct = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  if (Math.abs(n) > 999) return `${n > 0 ? '+' : ''}${(n / 100).toFixed(0)}×`;
  return `${n > 0 ? '+' : ''}${n.toFixed(1)}%`;
};

const healthBadge = (h) => {
  const map = {
    excellent: ['emerald', 'Excellent'],
    good: ['sky', 'Good'],
    warning: ['amber', 'Warning'],
    critical: ['rose', 'Critical'],
  };
  const [c, lbl] = map[h] || ['slate', h];
  return <Badge className={`bg-${c}-100 text-${c}-700 border-${c}-300 dark:bg-${c}-500/15 dark:text-${c}-300 dark:border-${c}-500/30`} data-testid={`health-badge-${h}`}>{lbl}</Badge>;
};

const CustomerPL = () => {
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [overview, setOverview] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [activeDetail, setActiveDetail] = useState(null);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('margin');
  const [sortDir, setSortDir] = useState('desc');
  const [throttledIds, setThrottledIds] = useState(new Set());
  const [throttleBusy, setThrottleBusy] = useState(null);

  const loadThrottles = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/customer-throttle/list`);
      setThrottledIds(new Set((data?.throttles || []).map((t) => t.customer_id)));
    } catch (e) { /* silent */ }
  };

  const handleThrottle = async (cust, e) => {
    if (e) e.stopPropagation();
    setThrottleBusy(cust.id);
    try {
      await axios.post(`${API_URL}/customer-throttle/throttle`, {
        customer_id: cust.id,
        customer_name: cust.name,
        action: 'rate_limit',
        reason: `Negative margin ${fmtCurrency(cust.margin)}/mo · ${cust.plan} plan`,
        rate_limit_rpm: 30,
        monthly_loss_usd: Math.abs(cust.margin || 0),
      });
      setThrottledIds(new Set([...throttledIds, cust.id]));
      toast.success(`${cust.name} throttled`, { description: 'AI usage now rate-limited to 30 RPM. Audit-logged.' });
    } catch (err) {
      toast.error('Throttle failed', { description: err?.response?.data?.detail || err?.message });
    } finally {
      setThrottleBusy(null);
    }
  };

  const handleUnthrottle = async (cust, e) => {
    if (e) e.stopPropagation();
    setThrottleBusy(cust.id);
    try {
      await axios.post(`${API_URL}/customer-throttle/unthrottle`, {
        customer_id: cust.id,
        reason: 'Manual lift via Customer P&L',
      });
      const next = new Set(throttledIds);
      next.delete(cust.id);
      setThrottledIds(next);
      toast.success(`${cust.name} unthrottled`);
    } catch (err) {
      toast.error('Unthrottle failed', { description: err?.response?.data?.detail || err?.message });
    } finally {
      setThrottleBusy(null);
    }
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [oRes, sRes] = await Promise.all([
        axios.get(`${API_URL}/customer-pl/overview`),
        axios.get(`${API_URL}/customer-pl/sync-status`),
      ]);
      setOverview(oRes.data);
      setSyncStatus(sRes.data);
    } catch (e) {
      toast.error('Could not load Customer P&L', { description: e?.response?.data?.detail || 'Please try again.' });
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); loadThrottles(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, []);

  const handleSalesAlert = async () => {
    try {
      const token = localStorage.getItem('token');
      toast.message('Firing sales-margin alert…', { description: 'Cross-functional FinOps in action.' });
      const res = await fetch(`${API_URL}/customer-pl/sales-alert/test`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      const customer = data.customer || data.results?.[0]?.customer_name;
      toast.success(`Sales alert sent (${data.mode})`, {
        description: customer
          ? `Account owner pinged about ${customer}. Check your sales Slack channel.`
          : data.note || 'No active losers right now — try the demo dataset.',
      });
    } catch (err) {
      toast.error('Sales alert failed', { description: err?.message });
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data } = await axios.post(`${API_URL}/customer-pl/sync-stripe`);
      if (data.ok === false) toast.error('Stripe sync failed', { description: data.message || data.error });
      else { toast.success(data.demo ? 'Demo data refreshed' : 'Stripe data synced', { description: `${data.customers_synced} customers cached` }); await loadAll(); }
    } catch (e) { toast.error('Sync failed', { description: e?.response?.data?.detail || 'Please try again.' }); }
    finally { setSyncing(false); }
  };

  const handleAlertUnprofitable = async () => {
    try {
      const { data } = await axios.post(`${API_URL}/customer-pl/check-unprofitable`);
      if (data.alerts_sent > 0) toast.success(`${data.alerts_sent} Slack alert${data.alerts_sent === 1 ? '' : 's'} fired`);
      else toast.message('No new alerts', { description: 'All unprofitable customers already flagged.' });
    } catch (e) { toast.error('Alert run failed'); }
  };

  const handleSendDigest = async () => {
    try {
      const { data } = await axios.post(`${API_URL}/customer-pl/digest/send-now`);
      if (data?.sent) toast.success('Monday digest sent to Slack', { description: data.fallback });
      else toast.error('Digest send failed', { description: 'Check Slack webhook config.' });
    } catch (e) { toast.error('Digest failed'); }
  };

  const handleEmailDigest = async () => {
    try {
      const { data } = await axios.post(`${API_URL}/customer-pl/digest/email-now`);
      if (data?.sent) toast.success(`Email sent to ${data.to}`, { description: data.subject });
      else toast.message('Email queued (demo mode)', { description: 'No live email backend configured — preview only.' });
    } catch (e) { toast.error('Email failed'); }
  };

  const handleCsvUpload = async (e) => {
    const f = e?.target?.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please select a .csv file from Stripe → Reports');
      e.target.value = '';
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', f);
      toast.message('Importing CSV…', { description: 'Parsing in memory — never written to disk.' });
      const res = await fetch(`${API_URL}/customer-pl/upload-stripe-csv`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      toast.success(data.message || 'Imported', {
        description: `Looks like a ${data?.detected?.looks_like || 'customer'} export. ${data.rows_inserted} new + ${data.rows_updated} updated.`,
      });
      await loadAll();
    } catch (err) {
      toast.error('CSV import failed', { description: err?.message });
    } finally {
      e.target.value = '';
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/customer-pl/export.csv`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `customer-pl-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (e) { toast.error('Export failed'); }
  };

  const handleExportPDF = async () => {
    try {
      const token = localStorage.getItem('token');
      toast.message('Building your boardroom PDF…', { description: 'Just a moment.' });
      const res = await fetch(`${API_URL}/customer-pl/export.pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `customer-pl-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success('Boardroom PDF downloaded', { description: 'Forward it straight to your CFO or CEO.' });
    } catch (e) { toast.error('PDF export failed', { description: e?.message }); }
  };

  const openDetail = async (id) => {
    setActiveId(id); setActiveDetail(null);
    try { const { data } = await axios.get(`${API_URL}/customer-pl/customer/${id}`); setActiveDetail(data); }
    catch (e) { toast.error('Could not load customer detail'); }
  };

  const filteredRows = useMemo(() => {
    if (!overview) return [];
    let rows = overview.customers || [];
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter((r) => r.name?.toLowerCase().includes(q) || r.email?.toLowerCase().includes(q) || r.plan?.toLowerCase().includes(q));
    }
    rows = [...rows].sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      const av = a[sortKey] ?? 0, bv = b[sortKey] ?? 0;
      if (typeof av === 'string') return av.localeCompare(bv) * dir;
      return (av - bv) * dir;
    });
    return rows;
  }, [overview, search, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  if (loading) {
    return (
      <div className="p-8 space-y-4" data-testid="customer-pl-loading">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-4 gap-4">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-32" />)}</div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  const summary = overview?.summary || {};
  const isDemo = overview?.data_source === 'demo';
  const lastSync = overview?.last_sync || syncStatus?.last_sync;

  return (
    <div className="min-h-screen bg-white text-slate-900 -mt-px">
    <SEOHead
      title="Customer P&L — Stripe revenue × cloud cost per tenant"
      description="See exactly which customers cost more than they pay you. Stripe-synced MRR vs cloud + LLM cost per tenant, flagged unprofitable accounts, throttling controls."
    />
    <div className="p-6 sm:p-8 space-y-6 max-w-7xl mx-auto" data-testid="customer-pl-page" data-tour="pl-table">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Customer <span className="text-emerald-600">P&L</span></h1>
            <Badge className="bg-emerald-100 border border-emerald-300 text-emerald-800 font-semibold" data-testid="stripe-powered-badge">
              <Zap className="h-3 w-3 mr-1" />Stripe-powered
            </Badge>
            {isDemo && <Badge variant="outline" className="border-amber-300 text-amber-800 bg-amber-50 font-semibold" data-testid="demo-mode-badge">Demo data</Badge>}
          </div>
          <p className="text-base text-slate-600 max-w-2xl">See exactly which customers are <span className="text-emerald-700 font-semibold">profitable</span> — and which are <span className="text-rose-700 font-semibold">silently bleeding money</span>. Live Stripe revenue joined with your cloud cost data, per tenant.</p>
          {lastSync && (
            <p className="text-xs text-slate-500 mt-2 flex items-center gap-1.5" data-testid="last-sync-time">
              <Activity className="h-3 w-3 text-emerald-600" />Last synced {new Date(lastSync).toLocaleString()}
              {syncStatus?.customers_cached !== undefined && <> · {syncStatus.customers_cached} customers cached</>}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={handleAlertUnprofitable} data-testid="alert-unprofitable-btn" className="border-emerald-500/40 text-emerald-300 hover:border-emerald-500 hover:bg-emerald-500/10"><Bell className="h-4 w-4 mr-2" />Alert Slack</Button>
          <Button variant="outline" onClick={handleSendDigest} data-testid="send-digest-btn" className="border-emerald-500/40 text-emerald-300 hover:border-emerald-500 hover:bg-emerald-500/10"><Activity className="h-4 w-4 mr-2" />Send digest</Button>
          <Button variant="outline" onClick={handleEmailDigest} data-testid="email-digest-btn" className="border-emerald-500/40 text-emerald-300 hover:border-emerald-500 hover:bg-emerald-500/10"><Bell className="h-4 w-4 mr-2" />Email me</Button>
          <Button variant="outline" onClick={handleExport} data-testid="export-csv-btn" className="border-emerald-500/40 text-emerald-300 hover:border-emerald-500 hover:bg-emerald-500/10"><Download className="h-4 w-4 mr-2" />Export CSV</Button>
          <Button variant="outline" onClick={handleSalesAlert} data-testid="sales-alert-btn" className="border-rose-500/40 hover:border-rose-500 text-rose-300 hover:bg-rose-500/10"><Bell className="h-4 w-4 mr-2" />Alert Sales</Button>
          <Button variant="outline" onClick={handleExportPDF} data-testid="export-pdf-btn" className="border-emerald-500/40 hover:border-emerald-500 text-emerald-300 hover:bg-emerald-500/10"><FileDown className="h-4 w-4 mr-2" />Boardroom PDF</Button>
          <label className="cursor-pointer">
            <input type="file" accept=".csv" className="hidden" onChange={handleCsvUpload} data-testid="csv-upload-input" />
            <span className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md border border-emerald-500/40 text-emerald-300 bg-transparent hover:bg-emerald-500/10 hover:border-emerald-500" data-testid="csv-upload-btn">
              <Upload className="h-4 w-4 mr-2" />Import Stripe CSV
            </span>
          </label>
          <Button onClick={handleSync} disabled={syncing} data-testid="sync-stripe-btn" className="bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-500/20">
            <RefreshCcw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />{syncing ? 'Syncing…' : 'Sync Stripe'}
          </Button>
        </div>
      </div>

      <BillDetectiveCrossLink
        testid="customer-pl-bd-crosslink"
        message="Customer P&L is Stripe-revenue × cloud-cost per tenant. For per-engineer / per-commit attribution →"
      />

      {/* Decision card — which P&L view should I use? */}
      <div className="rounded-lg border border-emerald-500/40 bg-emerald-50 bg-gradient-to-br from-emerald-50 to-cyan-50 dark:from-emerald-500/10 dark:to-cyan-500/5 p-4 sm:p-5" data-testid="pl-decision-card">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="text-[10px] uppercase tracking-widest text-emerald-700 dark:text-emerald-300 font-semibold">Which P&amp;L view should I use?</div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="p-3 rounded-md border-2 border-emerald-500/50 bg-white dark:bg-slate-950 text-slate-100">
            <div className="text-xs font-semibold text-emerald-700 dark:text-emerald-300">📊 You're here · Customer P&amp;L</div>
            <div className="text-xs text-slate-300 dark:text-slate-400 mt-1.5"><strong>Best for FinOps leads</strong> — monthly accounting view: who's profitable, who's losing money, which deal is bleeding margin.</div>
          </div>
          <Link to="/ai-feature-profitability" className="p-3 rounded-md border border-slate-200 dark:border-white/10 bg-white hover:border-emerald-500/40 hover:bg-emerald-50 dark:hover:bg-emerald-500/5 transition-colors block">
            <div className="text-xs font-semibold text-slate-900 dark:text-white">⚡ AI Feature Profitability →</div>
            <div className="text-xs text-slate-600 dark:text-slate-400 mt-1.5"><strong>Best for product leads</strong> — per-feature margin: which AI feature is paying for itself, which is a money pit.</div>
          </Link>
          <Link to="/unit-econ" className="p-3 rounded-md border border-slate-200 dark:border-white/10 bg-white hover:border-emerald-500/40 hover:bg-emerald-50 dark:hover:bg-emerald-500/5 transition-colors block">
            <div className="text-xs font-semibold text-slate-900 dark:text-white">🔬 Real-Time Unit Econ →</div>
            <div className="text-xs text-slate-600 dark:text-slate-400 mt-1.5"><strong>Best for engineering leads</strong> — per-request streaming: live cost per trace span × customer × feature.</div>
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-grid">
        <Card className="border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 to-transparent" data-testid="kpi-profitable">
          <CardContent className="pt-6"><div className="flex items-center justify-between mb-2"><CheckCircle2 className="h-5 w-5 text-emerald-500" /><span className="text-xs text-muted-foreground uppercase tracking-wide">Profitable</span></div><div className="text-3xl font-bold text-emerald-600 dark:text-emerald-300">{summary.profitable_customers || 0}</div><div className="text-xs text-muted-foreground mt-1">of {summary.total_customers || 0} customers</div></CardContent>
        </Card>
        <Card className="border-rose-500/40 bg-gradient-to-br from-rose-500/10 to-transparent" data-testid="kpi-unprofitable">
          <CardContent className="pt-6"><div className="flex items-center justify-between mb-2"><XCircle className="h-5 w-5 text-rose-500" /><span className="text-xs text-muted-foreground uppercase tracking-wide">Losing money</span></div><div className="text-3xl font-bold text-rose-600 dark:text-rose-300">{summary.unprofitable_customers || 0}</div><div className="text-xs text-muted-foreground mt-1">{fmtCurrency(summary.monthly_loss_from_unprofitable)}/mo bleed</div></CardContent>
        </Card>
        <Card className="border-amber-500/40 bg-gradient-to-br from-amber-500/10 to-transparent" data-testid="kpi-annualized-loss">
          <CardContent className="pt-6"><div className="flex items-center justify-between mb-2"><AlertTriangle className="h-5 w-5 text-amber-500" /><span className="text-xs text-muted-foreground uppercase tracking-wide">Annualized loss</span></div><div className="text-3xl font-bold text-amber-600 dark:text-amber-300">{fmtCurrency(summary.annualized_loss)}</div><div className="text-xs text-muted-foreground mt-1">if unprofitable customers continue</div></CardContent>
        </Card>
        <Card data-testid="kpi-avg-margin">
          <CardContent className="pt-6"><div className="flex items-center justify-between mb-2"><TrendingUp className="h-5 w-5 text-emerald-500" /><span className="text-xs text-muted-foreground uppercase tracking-wide">Blended margin</span></div><div className="text-3xl font-bold text-emerald-600 dark:text-cyan-300">{(summary.avg_margin_percent || 0).toFixed(1)}%</div><div className="text-xs text-muted-foreground mt-1">MRR {fmtCurrency(summary.total_mrr)} · Cost {fmtCurrency(summary.total_cloud_cost)}</div></CardContent>
        </Card>
      </div>

      <Tabs defaultValue="all" className="space-y-4" data-testid="pl-tabs">
        <TabsList>
          <TabsTrigger value="all" data-testid="tab-all-customers">All customers</TabsTrigger>
          <TabsTrigger value="losers" data-testid="tab-money-losers">Money losers{summary.unprofitable_customers > 0 && <span className="ml-2 px-1.5 py-0.5 rounded-full bg-rose-500 text-white text-[11px] font-bold">{summary.unprofitable_customers}</span>}</TabsTrigger>
          <TabsTrigger value="winners" data-testid="tab-top-performers">Top performers</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-4">
          <div className="flex items-center gap-2 max-w-md">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search customer, email, plan…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="customer-search-input" />
            </div>
          </div>
          <Card data-testid="customer-table-card">
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground"><tr>
                  {[['name', 'Customer'], ['plan', 'Plan'], ['mrr', 'MRR'], ['cloud_cost', 'Cloud Cost'], ['margin', 'Margin'], ['margin_percent', 'Margin %'], ['health', 'Health']].map(([k, label]) => (
                    <th key={k} className="px-4 py-3 text-left font-semibold cursor-pointer hover:text-foreground transition" onClick={() => toggleSort(k)} data-testid={`sort-${k}`}>
                      {label}{sortKey === k && <span className="ml-1 text-emerald-400">{sortDir === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                  ))}
                  <th className="px-4 py-3"></th>
                </tr></thead>
                <tbody>
                  {filteredRows.length === 0 && <tr><td colSpan={8} className="px-4 py-12 text-center text-muted-foreground">{overview?._empty_state ? <div className="space-y-2"><Sparkles className="h-8 w-8 mx-auto opacity-40" /><p>No Stripe data yet. Hit <strong>Sync Stripe</strong> to pull your customers.</p></div> : <p>No customers match your search.</p>}</td></tr>}
                  {filteredRows.map((r, rIdx) => (
                    <tr key={`${r.id || 'cust'}-${rIdx}`} className="border-t border-border/40 hover:bg-muted/30 cursor-pointer transition" onClick={() => openDetail(r.id)} data-testid={`customer-row-${r.id}`}>
                      <td className="px-4 py-3"><div className="font-semibold">{r.name}</div><div className="text-xs text-muted-foreground">{r.email}</div></td>
                      <td className="px-4 py-3">{r.plan}</td>
                      <td className="px-4 py-3 font-mono">{fmtCurrency(r.mrr)}</td>
                      <td className="px-4 py-3 font-mono">{fmtCurrency(r.cloud_cost)}</td>
                      <td className={`px-4 py-3 font-mono font-semibold ${r.margin < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}>{fmtCurrency(r.margin)}</td>
                      <td className="px-4 py-3 font-mono">{fmtPct(r.margin_percent)}</td>
                      <td className="px-4 py-3">{healthBadge(r.health)}</td>
                      <td className="px-4 py-3"><ChevronRight className="h-4 w-4 text-muted-foreground" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="losers" className="space-y-4">
          <div className="grid gap-4">
            {(overview?.money_losers || []).length === 0 ? (
              <Card><CardContent className="py-12 text-center text-muted-foreground"><CheckCircle2 className="h-10 w-10 mx-auto text-emerald-400 mb-3" /><p className="font-semibold text-emerald-300">No money-losing customers</p></CardContent></Card>
            ) : (overview?.money_losers || []).map((r, lIdx) => {
              const isThrottled = throttledIds.has(r.id);
              const busy = throttleBusy === r.id;
              return (
              <Card key={`${r.id || 'loser'}-${lIdx}`} className="border-rose-500/30 bg-gradient-to-r from-rose-500/5 to-transparent cursor-pointer hover:border-rose-500" onClick={() => openDetail(r.id)} data-testid={`loser-card-${r.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2"><XCircle className="h-5 w-5 text-rose-400" /><h3 className="text-lg font-bold">{r.name}</h3>{healthBadge(r.health)}{isThrottled && <Badge className="bg-amber-500/20 text-amber-700 border-amber-500/40 dark:text-amber-300" data-testid={`throttled-badge-${r.id}`}><ShieldOff className="h-3 w-3 mr-1" />Throttled</Badge>}</div>
                      <p className="text-sm text-muted-foreground mt-1">{r.plan} plan · paying <span className="font-mono">{fmtCurrency(r.mrr)}</span>/mo, costing <span className="font-mono text-rose-400">{fmtCurrency(r.cloud_cost)}</span>/mo</p>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-2xl font-bold text-rose-400 font-mono">{fmtCurrency(r.margin)}</div>
                      <div className="text-xs text-muted-foreground">monthly bleed</div>
                      {!isThrottled ? (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busy}
                          onClick={(e) => handleThrottle(r, e)}
                          className="mt-2 h-7 text-xs border-amber-500/40 text-amber-700 hover:bg-amber-50 dark:text-amber-300 dark:hover:bg-amber-500/10"
                          data-testid={`throttle-btn-${r.id}`}
                        >
                          <ShieldOff className="h-3 w-3 mr-1" />
                          {busy ? 'Throttling…' : 'Throttle AI'}
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busy}
                          onClick={(e) => handleUnthrottle(r, e)}
                          className="mt-2 h-7 text-xs border-emerald-500/40 text-emerald-700 hover:bg-emerald-50 dark:text-emerald-300 dark:hover:bg-emerald-500/10"
                          data-testid={`unthrottle-btn-${r.id}`}
                        >
                          <ShieldCheck className="h-3 w-3 mr-1" />
                          {busy ? '…' : 'Unthrottle'}
                        </Button>
                      )}
                    </div>
                  </div>
                  {r.recommendations?.[0] && <div className="mt-4 p-3 rounded-md bg-rose-500/10 border border-rose-500/20"><div className="flex items-start gap-2"><Sparkles className="h-4 w-4 text-rose-300 mt-0.5 shrink-0" /><p className="text-sm">{r.recommendations[0].message}</p></div></div>}
                </CardContent>
              </Card>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="winners" className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            {(overview?.top_performers || []).map((r, wIdx) => (
              <Card key={`${r.id || 'winner'}-${wIdx}`} className="border-emerald-500/30 bg-gradient-to-r from-emerald-500/5 to-transparent cursor-pointer hover:border-emerald-500" onClick={() => openDetail(r.id)} data-testid={`winner-card-${r.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div><div className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-400" /><h3 className="font-bold">{r.name}</h3></div><p className="text-xs text-muted-foreground">{r.plan} · {r.segment}</p></div>
                    <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30">{r.margin_percent.toFixed(1)}%</Badge>
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                    <div><div className="text-muted-foreground">MRR</div><div className="font-mono font-semibold">{fmtCurrency(r.mrr)}</div></div>
                    <div><div className="text-muted-foreground">Cost</div><div className="font-mono">{fmtCurrency(r.cloud_cost)}</div></div>
                    <div><div className="text-muted-foreground">LTV (36m)</div><div className="font-mono text-emerald-300">{fmtCurrency(r.ltv_36m)}</div></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {activeId && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={() => { setActiveId(null); setActiveDetail(null); }} data-testid="customer-drilldown-overlay">
          <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <CardHeader><div className="flex items-center justify-between">
              <div><CardTitle className="text-2xl flex items-center gap-2" data-testid="drilldown-customer-name">{activeDetail?.customer?.name || 'Loading…'}{activeDetail?.customer?.health && healthBadge(activeDetail.customer.health)}</CardTitle><CardDescription className="text-slate-400">{activeDetail?.customer?.plan} · {activeDetail?.customer?.email}</CardDescription></div>
              <Button variant="ghost" size="sm" onClick={() => { setActiveId(null); setActiveDetail(null); }} data-testid="close-drilldown-btn">✕</Button>
            </div></CardHeader>
            <CardContent className="space-y-5">
              {!activeDetail ? <Skeleton className="h-64" /> : (
                <>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div className="p-3 rounded-md bg-muted/40"><div className="text-xs text-muted-foreground uppercase">MRR</div><div className="text-xl font-bold font-mono">{fmtCurrency(activeDetail.customer.mrr)}</div></div>
                    <div className="p-3 rounded-md bg-muted/40"><div className="text-xs text-muted-foreground uppercase">Cloud Cost</div><div className="text-xl font-bold font-mono">{fmtCurrency(activeDetail.customer.cloud_cost)}</div></div>
                    <div className={`p-3 rounded-md ${activeDetail.customer.margin < 0 ? 'bg-rose-500/10' : 'bg-emerald-500/10'}`}><div className="text-xs text-muted-foreground uppercase">Margin</div><div className={`text-xl font-bold font-mono ${activeDetail.customer.margin < 0 ? 'text-rose-400' : 'text-emerald-400'}`}>{fmtCurrency(activeDetail.customer.margin)}</div></div>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold mb-2 flex items-center gap-2"><TrendingUp className="h-4 w-4" />6-Month Trend</h3>
                    <div className="h-56">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={activeDetail.trends}>
                          <defs>
                            <linearGradient id="g-mrr" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10b981" stopOpacity={0.6} /><stop offset="100%" stopColor="#10b981" stopOpacity={0} /></linearGradient>
                            <linearGradient id="g-cost" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#f43f5e" stopOpacity={0.6} /><stop offset="100%" stopColor="#f43f5e" stopOpacity={0} /></linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
                          <XAxis dataKey="month" stroke="#ffffff60" /><YAxis stroke="#ffffff60" tickFormatter={(v) => fmtCurrency(v)} />
                          <Tooltip contentStyle={{ background: '#0c0a18', border: '1px solid #ffffff20', borderRadius: 8 }} formatter={(v) => fmtCurrency(v)} />
                          <Legend />
                          <Area type="monotone" dataKey="mrr" stroke="#10b981" fill="url(#g-mrr)" name="MRR" />
                          <Area type="monotone" dataKey="cloud_cost" stroke="#f43f5e" fill="url(#g-cost)" name="Cloud cost" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  {activeDetail.recommendations?.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold mb-2 flex items-center gap-2"><Sparkles className="h-4 w-4 text-cyan-400" />AI Recommendations</h3>
                      <div className="space-y-2">{activeDetail.recommendations.map((rec, i) => (
                        <div key={i} className={`p-3 rounded-md border ${rec.priority === 'critical' ? 'border-rose-500/40 bg-rose-500/10' : rec.priority === 'high' ? 'border-amber-500/40 bg-amber-500/10' : 'border-emerald-500/30 bg-emerald-500/5'}`} data-testid={`recommendation-${i}`}>
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className={rec.priority === 'critical' ? 'bg-rose-500/20 text-rose-300 border-rose-500/40' : rec.priority === 'high' ? 'bg-amber-500/20 text-amber-300 border-amber-500/40' : 'bg-emerald-500/20 text-cyan-300 border-emerald-500/40'}>{rec.priority}</Badge>
                            <span className="text-xs uppercase tracking-wide text-muted-foreground">{rec.type.replace(/_/g, ' ')}</span>
                          </div>
                          <p className="text-sm">{rec.message}</p>
                        </div>
                      ))}</div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
    </div>
  );
};

export default CustomerPL;
